<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import PlusCircleIcon from '@iconify-vue/ph/plus-circle-fill';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { archived } = defineProps({
    archived: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                props.row.original.url
                    ? h(LinkCell, {
                          text: props.row.original.name,
                          href: props.row.original.url,
                      })
                    : h(TextCell, {
                          text: props.row.original.name,
                      }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            header: () => 'Type',
            cell: (props) =>
                props.row.original.type
                    ? h(TextCell, {
                          text: props.row.original.type.label,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'details',
            header: 'Détails',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.original.internalNotes
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Notes internes',
                                          content:
                                              props.row.original.internalNotes,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir les notes internes',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : null,
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'contactName',
            header: () => 'Nom',
            cell: (props) =>
                props.row.original.contacts.length > 0 &&
                props.row.original.contacts[0].name
                    ? h(TextCell, {
                          class: 'uppercase',
                          text: props.row.original.contacts[0].name,
                          tooltipIcon: PlusCircleIcon,
                          tooltipPlacement: 'bottom',
                          tooltipContent:
                              props.row.original.contacts.length > 1 &&
                              props.row.original.contacts
                                  .slice(1)
                                  .filter((contact) => contact.name).length > 0
                                  ? h(
                                        'ul',
                                        {
                                            class: 'list-disc list-inside !m-0',
                                        },
                                        [
                                            ...props.row.original.contacts
                                                .slice(1)
                                                .filter(
                                                    (contact) => contact.name,
                                                )
                                                .map((contact) =>
                                                    h('li', {
                                                        innerHTML: contact.name,
                                                    }),
                                                ),
                                        ],
                                    )
                                  : null,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'contactEmail',
            header: () => 'Email',
            cell: (props) =>
                props.row.original.contacts.length > 0 &&
                props.row.original.contacts[0].email
                    ? h(TextCell, {
                          text: props.row.original.contacts[0].email,
                          tooltipIcon: PlusCircleIcon,
                          tooltipPlacement: 'bottom',
                          tooltipContent:
                              props.row.original.contacts.length > 1 &&
                              props.row.original.contacts
                                  .slice(1)
                                  .filter((contact) => contact.email).length > 0
                                  ? h(
                                        'ul',
                                        {
                                            class: 'list-disc list-inside !m-0 px-2 py-1',
                                        },
                                        [
                                            ...props.row.original.contacts
                                                .slice(1)
                                                .filter(
                                                    (contact) => contact.email,
                                                )
                                                .map((contact) =>
                                                    h('li', {
                                                        innerHTML:
                                                            "<b class='uppercase'>" +
                                                            contact.name +
                                                            ' :</b> ' +
                                                            contact.email,
                                                    }),
                                                ),
                                        ],
                                    )
                                  : null,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'contactPhone',
            header: () => 'Téléphone',
            cell: (props) =>
                props.row.original.contacts.length > 0 &&
                props.row.original.contacts[0].phone
                    ? h(TextCell, {
                          text: props.row.original.contacts[0].phone,
                          tooltipIcon: PlusCircleIcon,
                          tooltipPlacement: 'bottom',
                          tooltipContent:
                              props.row.original.contacts.length > 1 &&
                              props.row.original.contacts
                                  .slice(1)
                                  .filter((contact) => contact.phone).length > 0
                                  ? h(
                                        'ul',
                                        {
                                            class: 'list-disc list-inside !m-0 px-2 py-1',
                                        },
                                        [
                                            ...props.row.original.contacts
                                                .slice(1)
                                                .filter(
                                                    (contact) => contact.phone,
                                                )
                                                .map((contact) =>
                                                    h('li', {
                                                        innerHTML:
                                                            "<b class='uppercase'>" +
                                                            contact.name +
                                                            ' :</b> ' +
                                                            contact.phone,
                                                    }),
                                                ),
                                        ],
                                    )
                                  : null,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.apikom.updateAffiliation',
                                    {
                                        affiliation: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.apikom.postAffiliations'),
    data: {
        archived: archived,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.apikom.createAffiliation')
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
